import connect from 'components/Shared/connect';
import { compose } from 'redux';
import { ComponentType } from 'react';
import { AdTemplate } from 'models/AdTemplate';
import {
  createComposedAd,
  createImageAd,
  RedFullSize,
  RedHalfSize,
  RedQuarter,
  RedPanorama,
  RedLeaderboard,
  RedPanoramaImage,
  CurrentCampaign
} from './themes/BGH';
import { isLeaderBoardAd, LeaderBoardAd as LeaderBoardAdModel } from 'models/LeaderBoardAd';
import { ImageAd as ImageAdModel, isImageAd } from 'models/ImageAd';
import { isSliderAd, SliderAd as SliderAdModel } from 'models/SliderAd';
import { isMediumAd, MediumAd as MediumAdModel } from 'models/MediumAd';
import { Ad as AdModel, isAd } from 'models/Ad';
import { isRedAd, RedAd } from 'models/RedAd';
import { NO_ELEMENT } from 'utils';
import { ThemeMeta } from 'models/SectionBlock';
import { GtmPromotion } from 'store/googleTagManager/types';
import { GtmSelectPromotion, GtmViewPromotion } from 'store/googleTagManager/actions';

type AppearanceTypes = 'normal' | 'slimmed';

export interface MergedProps {
  onAdView: (gtmPromotion: GtmPromotion) => void;
  onAdClick: (gtmPromotion: GtmPromotion) => void;
}

export type ComposedAds = MediumAdModel | SliderAdModel;

interface BaseAdProps {
  ad: AdModel;
  className?: string;
  position?: number;
}
export interface ComposedAdProps extends BaseAdProps {
  ad: ComposedAds;
  gotoOfferText: string;
  shadow?: boolean;
  appearance?: AppearanceTypes;
}

export type ImageAds = SliderAdModel | LeaderBoardAdModel | ImageAdModel | MediumAdModel;
export interface ImageAdProps extends BaseAdProps {
  ad: ImageAds;
}

export interface RedAdProps extends BaseAdProps, ThemeMeta {
  ad: RedAd;
}

const creativeMap: { [key: number]: string } = {
  [AdTemplate.R3Panorama]: 'Panorama',
  [AdTemplate.R3PanoramaImage]: 'Panorama',
  [AdTemplate.R3FullSize]: 'FullSize',
  [AdTemplate.R3HalfSize]: 'HalfSize',
  [AdTemplate.R3Quarter]: 'QuarterSize',
  [AdTemplate.R3Leaderboard]: 'Leaderboard'
};

export type ImageAdType =
  | AdTemplate.Leaderboard
  | AdTemplate.PanoramaFull
  | AdTemplate.SmallImageOneThird
  | AdTemplate.SmallImageOneForth
  | AdTemplate.MediumImageTwoThirds
  | AdTemplate.MediumImageOneThird;

export type ComposedAdType = AdTemplate.Medium | AdTemplate.PanoramaTwoThirds | AdTemplate.SiteWide;

export const getCreative = (template: AdTemplate) => {
  return creativeMap[template] || '';
};

export const getSlot = (position: number) => `slot${position}`;

export const getImage = (ad: ImageAds) => {
  return (
    (ad as SliderAdModel).adImage ||
    (ad as LeaderBoardAdModel).leaderboardImage ||
    (ad as ImageAdModel).image ||
    (ad as MediumAdModel).adImage
  );
};

const enhance = connect<MergedProps>(undefined, dispatch => ({
  onAdView: (promotion: GtmPromotion) => dispatch(GtmViewPromotion(promotion)),
  onAdClick: (promotion: GtmPromotion) => dispatch(GtmSelectPromotion(promotion))
}));

const composedAd = compose<ComponentType<ComposedAdProps>>(enhance, createComposedAd);

const imageAd = compose<ComponentType<ImageAdProps>>(enhance, createImageAd);

const MediumStandardAd = composedAd(AdTemplate.Medium);
const MediumImageOneThirdAd = imageAd(AdTemplate.MediumImageOneThird);
const PanoramaTwoThirdsAd = composedAd(AdTemplate.PanoramaTwoThirds);
const LeaderBoardAd = imageAd(AdTemplate.Leaderboard);
const LeaderBoardLargeAd = imageAd(AdTemplate.LeaderboardLarge);
const PanoramaFullAd = imageAd(AdTemplate.PanoramaFull);
const PanoramaBrandContentAd = imageAd(AdTemplate.PanoramaBrandContent);
const SiteWideAd = composedAd(AdTemplate.SiteWide);
const SmallImageOneThirdAd = imageAd(AdTemplate.SmallImageOneThird);
const SmallImageOneForthAd = imageAd(AdTemplate.SmallImageOneForth);
const MediumImageTwoThirdAd = imageAd(AdTemplate.MediumImageTwoThirds);
export const ArredoMenuAd = imageAd(AdTemplate.Medium);

type AdTypes = ComposedAdProps | ImageAdProps | RedAdProps;
const Ads: { [key: number]: ComponentType<AdTypes> } = {
  [AdTemplate.MediumImageOneThird]: MediumImageOneThirdAd,
  [AdTemplate.Medium]: MediumStandardAd,
  [AdTemplate.PanoramaTwoThirds]: PanoramaTwoThirdsAd,
  [AdTemplate.Leaderboard]: LeaderBoardAd,
  [AdTemplate.LeaderboardLarge]: LeaderBoardLargeAd,
  [AdTemplate.PanoramaFull]: PanoramaFullAd,
  [AdTemplate.PanoramaBrandContent]: PanoramaBrandContentAd,
  [AdTemplate.SiteWide]: SiteWideAd,
  [AdTemplate.SmallImageOneThird]: SmallImageOneThirdAd,
  [AdTemplate.SmallImageOneForth]: SmallImageOneForthAd,
  [AdTemplate.MediumImageTwoThirds]: MediumImageTwoThirdAd,

  [AdTemplate.R3Panorama]: enhance(RedPanorama),
  [AdTemplate.R3FullSize]: enhance(RedFullSize),
  [AdTemplate.R3HalfSize]: enhance(RedHalfSize),
  [AdTemplate.R3Quarter]: enhance(RedQuarter),
  [AdTemplate.R3PanoramaImage]: enhance(RedPanoramaImage),
  [AdTemplate.R3CurrentCampaign]: enhance(CurrentCampaign),
  [AdTemplate.R3Leaderboard]: enhance(RedLeaderboard)
};

const isValidAd = (props: BaseAdProps): props is AdTypes =>
  [isMediumAd, isSliderAd, isLeaderBoardAd, isImageAd, isRedAd, isAd].some(v => v(props.ad));
export const Ad = (props: BaseAdProps | AdTypes) => {
  if (!isValidAd(props)) {
    return NO_ELEMENT;
  }
  const AdComponent = Ads[props.ad.adTemplate];
  return <AdComponent {...props} />;
};
